import * as _ from "lodash";

import { Reducer } from "@ctra/utils";

import { cleanup } from "../../utils/reducer";
import { LayoutState } from "./typings";
import { NormalizedLayout } from "./schemas";
import types from "./types";
import actions from "./actions";

export const initialState: LayoutState = {
  farms: {},
  entities: {
    cards: {},
    layers: {},
    pages: {}
  }
};

/**
 * Reducer of list of saved charts
 * @param state
 * @param action
 * @category Reducer
 */
export const layoutStateReducer: Reducer<LayoutState> = (state, action) => {
  const { type, payload } = action;

  switch (type) {
    case types.FETCH_SAVED_CHARTS.fulfilled:
    case types.UPDATE_TABS.fulfilled:
    case types.UPDATE_CHARTS.fulfilled: {
      const {
        result: { tabs: pages, key },
        entities: { layer, card, page }
      } = payload as NormalizedLayout;

      /**
       * Any card without layers is considered invalid
       * @type {any[]}
       */
      const invalidCardIDCollection = _.map(
        _.filter(card, ({ layers }) => {
          return !_.size(layers);
        }),
        "id"
      );

      /**
       * Only keep cards that have layers
       * @type {Omit<CardCollection, any>}
       */
      const validCards = _.omit(card, invalidCardIDCollection);

      /**
       * Remove invalid cards from pages
       * @type {{[p: string]: {cards: string[], id: string, groupName: string}}}
       */
      const validPages = _.mapValues(page, (entity) => {
        return {
          ...entity,
          cards: _.reject(entity.cards, (cardID) => {
            return _.includes(invalidCardIDCollection, cardID);
          })
        };
      });

      return {
        farms: {
          ...state.farms,
          [key]: pages
        },
        entities: {
          cards: {
            ...state.entities.cards,
            ...validCards
          },
          layers: {
            ...state.entities.layers,
            ...layer
          },
          pages: {
            ...state.entities.pages,
            ...validPages
          }
        }
      };
    }
    case types.ADD_CARD.fulfilled: {
      const {
        pageID,
        result,
        entities: { layer, card }
      } = payload as ReturnType<typeof actions.addCard.fulfill>["payload"];

      const page = _.get(state, ["entities", "pages", pageID]);

      if (!page) {
        throw new Error(`Page with ID ${pageID} not found.`);
      }

      return {
        ...state,
        entities: {
          ...state.entities,
          pages: {
            ...state.entities.pages,
            [pageID]: {
              ...page,
              cards: [...(page.cards || []), result]
            }
          },
          cards: {
            ...state.entities.cards,
            ...card
          },
          layers: {
            ...state.entities.layers,
            ...layer
          }
        }
      };
    }
    case types.ADD_TAB.fulfilled: {
      const {
        contextID,
        result,
        entities: { page }
      } = payload as ReturnType<typeof actions.addTab.fulfill>["payload"];

      return {
        ...state,
        farms: {
          ...state.farms,
          [contextID]: [...state.farms[contextID], result]
        },
        entities: {
          ...state.entities,
          pages: {
            ...state.entities.pages,
            ...page
          }
        }
      };
    }
  }

  return state;
};

export default cleanup<LayoutState>(initialState)(layoutStateReducer);
